/**
 * Picture-in-Picture state for the exam lock.
 *
 * Two sources can report PiP:
 *
 * 1. The page's own `<video>` — `document.pictureInPictureElement` and the
 *    `enter|leavepictureinpicture` events. This is all a browser exposes.
 * 2. A WebView host — a Picture-in-Picture window owned by ANOTHER application
 *    is invisible to the web platform (Android PiP is a multi-window mode:
 *    Chrome stays visible, resumed and focused, so no page-visible signal
 *    fires — see docs/ARCHITECTURE_AND_IMPLEMENTATION_PLAN.md §4.7). A native
 *    host CAN observe it, and the team's browser-state reference scheme
 *    (storage.googleapis.com/aimsis-files/yama/test/browser-states.html?v2)
 *    fixes the channel: the host calls
 *    `window.__nativeWindowState({ pip: true })` while PiP is active and
 *    `{ pip: false }` when it closes — the reference's "only detection that is
 *    certain" for that case.
 *
 * This module owns the web end of that contract and the combined predicate the
 * exam guard locks on. A report that arrives before the guard mounts is
 * retained and replayed to the subscriber on mount instead of being dropped.
 * The page owns the definition — a host only calls it — and the retained report
 * is cleared when the exam session ends (`resetReportedNativePip`), so one
 * attempt cannot lock the next.
 */

/** Last state the host reported, and when; null until it reports anything. */
let reportedPip: boolean | null = null
let reportedAtMs = 0

const listeners = new Set<(pip: boolean) => void>()

/**
 * How long a host report stays authoritative. The platform's only explicit
 * session bound is the make-up schedule endpoint's four hours (14400 s), so six
 * hours cannot expire while a scheduled exam is running; it still bounds the
 * case where a host reports once and then goes silent for good (killed, frozen,
 * or never sending the closing report): an aged-out report is treated as
 * unknown — not active — so it cannot pin a later attempt. A shorter window was
 * rejected: it would release the lock during a legitimate long floating window,
 * the failure this guard exists to prevent.
 */
const NATIVE_REPORT_TTL_MS = 6 * 60 * 60 * 1000

/** The host's last report, while it is still authoritative — else null. */
function freshReportedNativePip(): boolean | null {
  if (reportedPip === null) return null
  return Date.now() - reportedAtMs > NATIVE_REPORT_TTL_MS ? null : reportedPip
}

/**
 * The host's last reported PiP state, or null when it never reported one (or
 * the report has aged out of the trust window above). Deliberately distinct
 * from `document.pictureInPictureElement`, which only ever describes this
 * page's own video.
 */
export function readReportedNativePip(): boolean | null {
  return freshReportedNativePip()
}

/**
 * Observe host PiP reports. The listener is called immediately with the last
 * reported state when one exists, so a subscriber that starts after a report
 * cannot miss it. Returns an unsubscribe function.
 */
export function subscribeReportedNativePip(listener: (pip: boolean) => void): () => void {
  listeners.add(listener)
  const current = freshReportedNativePip()
  if (current !== null) listener(current)
  return () => {
    listeners.delete(listener)
  }
}

declare global {
  interface Window {
    /** Called by a WebView host with the platform's window state. */
    __nativeWindowState?: (state?: { pip?: boolean }) => void
  }
}

/**
 * Install the contract function. Called once at boot so a report arriving
 * while the SPA is still starting is kept rather than lost to a missing
 * function. Non-boolean `pip` values are ignored, matching the reference
 * implementation. The page owns this definition (a host only calls it);
 * re-installing preserves the retained report and the subscribers.
 */
export function installNativeWindowStateBridge(): void {
  window.__nativeWindowState = (state) => {
    const pip = state?.pip
    if (typeof pip !== 'boolean') return
    reportedPip = pip
    reportedAtMs = Date.now()
    for (const listener of [...listeners]) listener(pip)
  }
}

/**
 * Drop the retained host report — called when the exam session ends. The report
 * describes the device at the moment it arrived, so carrying it into the next
 * attempt would lock that attempt for a window that may no longer exist (the
 * guard's overlay has no dismiss control). The host re-reports on the next
 * state change. Subscribers are notified so a guard that is still mounted
 * releases with it.
 */
export function resetReportedNativePip(): void {
  reportedPip = null
  reportedAtMs = 0
  for (const listener of [...listeners]) listener(false)
}

/**
 * Whether the exam must lock for Picture-in-Picture: this page's own video, or
 * a window a WebView host reported through the bridge above — the
 * cross-application case no web API exposes.
 */
export function isPictureInPictureActive(): boolean {
  return Boolean(document.pictureInPictureElement) || freshReportedNativePip() === true
}
